import { useNavigation, NavigationProp, StackActions } from '@react-navigation/native';
import React, { useState, useCallback, createContext, useContext, useMemo } from 'react';

interface NavigationContextProps {
    navigationProps: Record<string, any>;
    updateNavigationProps: (newProps: React.SetStateAction<object>) => void;
    navigate: (location: string, data?: any, asset?: any) => void;
    push: (location: string, data?: any, asset?: any) => void;
}

const NavigationPropsContext = createContext<NavigationContextProps | undefined>(undefined);

export const useNavigationPropsContext = () => useContext(NavigationPropsContext);

const NavigationPropsContextProvider = ({ children }) => {
    const [navigationProps, setNavigationProps] = useState<object>({});
    const navigation = useNavigation<NavigationProp<any>>();

    const updateNavigationProps = useCallback((newProps: React.SetStateAction<object>) => {
        setNavigationProps((prevProps) => ({ ...prevProps, ...newProps }));
    }, []);

    const navigate = useCallback(
        (location: string, data?: object, asset?: any) => {
            if (asset !== undefined) {
                updateNavigationProps(data);
                navigation.navigate(location, {
                    id: asset.id,
                    type: asset.type,
                    platform: asset.platform,
                    parentId: asset?.parentId ?? '',
                    isPlanned: asset?.isPlanned || false
                });
            } else {
                navigation.navigate(location, data);
                updateNavigationProps(data);
            }
        },
        [navigation, updateNavigationProps]
    );

    const push = useCallback(
        (location: string, data?: object, asset?: any) => {
            if (asset !== undefined) {
                updateNavigationProps(data);
                navigation.dispatch(
                    StackActions.push(location, {
                        id: asset.id,
                        type: asset.type,
                        platform: asset.platform,
                        parentId: asset?.parentId ?? '',
                        isPlanned: asset?.isPlanned || false
                    })
                );
            } else {
                navigation.dispatch(StackActions.push(location, data));
                updateNavigationProps(data);
            }
        },
        [navigation, updateNavigationProps]
    );

    const memoizedProps: NavigationContextProps = useMemo(
        () => ({
            navigationProps,
            updateNavigationProps,
            navigate,
            push
        }),
        [navigationProps, updateNavigationProps, navigate, push]
    );

    return (
        <NavigationPropsContext.Provider value={memoizedProps}>
            {children}
        </NavigationPropsContext.Provider>
    );
};

export default NavigationPropsContextProvider;

export const withNavigationProps = (Component) => function WrapperComponent(props) {
    return (
        <NavigationPropsContext.Consumer>
            {(context) => {
                if (context === undefined) {
                    throw new Error('withNavigationProps must be used within a NavigationPropsContextProvider');
                }
                return <Component {...props} navigationPropsContext={context} />;
            }}
        </NavigationPropsContext.Consumer>
    );
};